import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  buildProspectStageOptions,
  getProspectStageOption,
  LOST_STAGE_DOT_COLOR,
} from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/utils/prospect-stage-options';
import {
  Button,
  DropdownMenu,
  Icons,
  Typography,
  View,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { ChatProspectStage } from '@/types';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { PROSPECT_STAGE_ICON_OUTLINE } from '@/experiences/sales-portal/domain/prospects/blocs/utils/prospect-stage-visuals';
import { useTheme } from '@/contexts/theme-context';
import type { ProspectStageVisual } from '@/experiences/sales-portal/domain/prospects/blocs/utils/prospect-stage-visuals';

const DROPDOWN_WIDTH = 220;
const ICON_SIZE = 14;
const LOST_DOT_SIZE = 8;
const STAGE_ICON_SIZE = 9;
const DROPDOWN_ITEM_HEIGHT = 36;

interface ProspectStageMarkerProps {
  visual: ProspectStageVisual;
}

export const ProspectStageMarker: React.FC<ProspectStageMarkerProps> = ({
  visual,
}) => {
  const StageIcon = visual.icon;

  return (
    <View height={STAGE_ICON_SIZE} width={STAGE_ICON_SIZE}>
      <StageIcon
        color={visual.color}
        fill={visual.isHollow ? 'transparent' : 'currentColor'}
        size={STAGE_ICON_SIZE}
      />
      <View left={0} pointerEvents="none" position="absolute" top={0}>
        <StageIcon
          color={PROSPECT_STAGE_ICON_OUTLINE.color}
          fill="transparent"
          size={STAGE_ICON_SIZE}
          strokeOpacity={PROSPECT_STAGE_ICON_OUTLINE.opacity}
        />
      </View>
    </View>
  );
};

interface ProspectStageMenuProps {
  onSelectLost: () => void;
  onSelectStage: (stage: ChatProspectStage) => void;
  stage: ChatProspectStage;
}

export const ProspectStageMenu: React.FC<ProspectStageMenuProps> = ({
  onSelectLost,
  onSelectStage,
  stage,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const rotationProgress = useDerivedValue(() =>
    withTiming(isOpen ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(rotationProgress.value, [0, 1], [0, 180])}deg` },
    ],
  }));

  const options = buildProspectStageOptions();
  const currentOption = getProspectStageOption(stage);

  const handleSelectStage = (nextStage: ChatProspectStage) => {
    setIsOpen(false);
    onSelectStage(nextStage);
  };

  const handleSelectLost = () => {
    setIsOpen(false);
    onSelectLost();
  };

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen} placement="bottom-end">
      <DropdownMenu.Trigger asChild>
        <Button
          aria-expanded={isOpen}
          aria-label={i18n.t(
            'sales_portal.prospect_detail.stage.trigger_label',
            { stage: currentOption.label },
          )}
          size="$sm"
          variant="secondary"
        >
          <XStack alignItems="center" gap="$8">
            <ProspectStageMarker visual={currentOption} />
            <Typography
              color={theme.sk.color.text.primary}
              fontWeight="$medium"
              size="$sm"
            >
              {currentOption.label}
            </Typography>
            <Animated.View style={chevronStyle}>
              <Icons.ChevronDown
                color={theme.sk.color.icon.tertiary}
                size={ICON_SIZE}
              />
            </Animated.View>
          </XStack>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content autoFocusFirstItem={false} width={DROPDOWN_WIDTH}>
        {options.map((option) => (
          <DropdownMenu.Item
            aria-selected={option.stage === stage}
            height={DROPDOWN_ITEM_HEIGHT}
            key={option.stage}
            onPress={() => handleSelectStage(option.stage)}
          >
            <XStack alignItems="center" flex={1} justifyContent="space-between">
              <XStack alignItems="center" gap="$10">
                <ProspectStageMarker visual={option} />
                <DropdownMenu.Item.Text flex={1} fontWeight="$medium">
                  {option.label}
                </DropdownMenu.Item.Text>
              </XStack>
              {option.stage === stage ? (
                <Icons.Check
                  color={theme.sk.color.icon.primary}
                  size={ICON_SIZE}
                />
              ) : null}
            </XStack>
          </DropdownMenu.Item>
        ))}
        <DropdownMenu.Item
          aria-selected={false}
          height={DROPDOWN_ITEM_HEIGHT}
          onPress={handleSelectLost}
        >
          <XStack alignItems="center" gap="$10">
            <View
              backgroundColor={LOST_STAGE_DOT_COLOR}
              borderRadius="$round"
              height={LOST_DOT_SIZE}
              width={LOST_DOT_SIZE}
            />
            <DropdownMenu.Item.Text destructive flex={1} fontWeight="$medium">
              {i18n.t('sales_portal.prospect_detail.stage.lost')}
            </DropdownMenu.Item.Text>
          </XStack>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
